import React, { useCallback, useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useSearchParams } from 'react-router-dom';
import ContractList from '../components/users/ContractList';
import ContractPdfViewer from '../components/users/ContractPdfViewer';
import UserDetailPanel from '../components/users/UserDetailPanel';
import UserList from '../components/users/UserList';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useOfficeExportToggle from '../hooks/useOfficeExportToggle';
import useUserContracts from '../hooks/useUserContracts';
import useUserDetail from '../hooks/useUserDetail';
import useUsers from '../hooks/useUsers';
import type { ContractSummary, SortDirection, UserSortField } from '../services/types';
import type { UserListQuery } from '../services/userService';

/** Query-Parameter, in dem die gewaehlte Benutzer-ID steht (Deep-Link). */
const USER_QUERY_PARAM = 'user';

/** Debounce des Suchfeldes in Millisekunden. */
const SEARCH_DEBOUNCE_MS = 400;

/** Voreingestellte Seitengroesse (Backend-Default). */
const DEFAULT_PAGE_SIZE = 25;

/** Voreingestellte Sortierung: neueste Registrierungen zuerst. */
const DEFAULT_SORT_FIELD: UserSortField = 'createdAt';
const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

/**
 * Standard-Sortierrichtung eines Feldes: Zeitangaben absteigend (neueste
 * zuerst), Texte aufsteigend (A-Z).
 */
function defaultDirectionFor(field: UserSortField): SortDirection {
  return field === 'createdAt' ? 'desc' : 'asc';
}

/**
 * Benutzerverwaltung als Master-Detail-Ansicht:
 * links die Liste (Suche, Sortierung, Pagination), rechts Stammdaten und
 * Mietvertraege des gewaehlten Benutzers. Auf schmalen Viewports stapeln
 * sich die Spalten, der Detailbereich erscheint dann unter der Liste.
 *
 * Die gewaehlte Benutzer-ID steht im URL-Query (?user=<id>), damit
 * Deep-Links funktionieren. Die Auswahl ersetzt den History-Eintrag
 * (replace), damit die Zurueck-Navigation die Seite verlaesst und nicht
 * jede einzelne Zeilenauswahl rueckwaerts durchlaeuft.
 *
 * Der Abfragezustand der Liste (Suche, Seite, Groesse, Sortierung) liegt
 * bewusst hier und nicht in der Liste selbst: So bleiben die
 * Unterkomponenten rein darstellend, und alle Requests haengen an EINEM
 * Zustand.
 *
 * Der Abfragezustand ist EIN Objekt (nicht mehrere Einzel-States): Suche
 * und Seitenreset muessen zusammen in einem Update landen. Waeren es
 * zwei Updates, feuerte der Seitenreset sofort einen Request mit dem
 * ALTEN (noch nicht debouncten) Suchbegriff — ein Extra-Request und ein
 * kurzes Aufblitzen der ungefilterten Liste.
 */
function UsersPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  // Ein leerer Parameter (/benutzer?user=) ist KEINE Auswahl: Er wuerde
  // sonst einen Request auf /api/users/ ausloesen und im Detailbereich
  // einen Fehlerzustand erzeugen.
  const userParam = searchParams.get(USER_QUERY_PARAM);
  const selectedUserId = userParam !== null && userParam.trim() !== '' ? userParam : null;

  const [searchInput, setSearchInput] = useState<string>('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [query, setQuery] = useState<UserListQuery>({
    search: '',
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sortField: DEFAULT_SORT_FIELD,
    sortDirection: DEFAULT_SORT_DIRECTION,
  });

  const [pdfContract, setPdfContract] = useState<ContractSummary | null>(null);

  /**
   * Uebernimmt den debouncten Suchbegriff zusammen mit dem Seitenreset in
   * EINEM Update — daraus entsteht genau ein Listen-Request. Der Vergleich
   * verhindert ein ueberfluessiges Update, wenn der Wert bereits gesetzt
   * ist (z. B. weil "Suche zurücksetzen" ihn sofort uebernommen hat).
   */
  useEffect(() => {
    setQuery((previous) =>
      previous.search === debouncedSearch
        ? previous
        : { ...previous, search: debouncedSearch, page: 0 }
    );
  }, [debouncedSearch]);

  const users = useUsers(query);
  const detail = useUserDetail(selectedUserId);
  const officeExport = useOfficeExportToggle(selectedUserId, detail.applyOfficeExport);
  const contracts = useUserContracts(selectedUserId);

  /**
   * Sucheingabe: nur das Eingabefeld aktualisieren. Der Abfragezustand
   * folgt verzoegert ueber den Debounce-Effekt oben.
   */
  const handleSearchChange = useCallback((value: string): void => {
    setSearchInput(value);
  }, []);

  /**
   * "Suche zurücksetzen": wirkt sofort und umgeht den Debounce bewusst —
   * ein bewusster Klick soll nicht 400 ms auf die ungefilterte Liste
   * warten lassen.
   */
  const handleSearchClear = useCallback((): void => {
    setSearchInput('');
    setQuery((previous) =>
      previous.search === '' ? previous : { ...previous, search: '', page: 0 }
    );
  }, []);

  /**
   * Klick auf einen Spaltenkopf: gleiches Feld dreht die Richtung um,
   * ein neues Feld startet mit seiner Standardrichtung. Die Seite springt
   * dabei zurueck auf die erste, weil sich die Reihenfolge komplett
   * aendert.
   */
  const handleSortChange = useCallback((field: UserSortField): void => {
    setQuery((previous) => {
      if (field === previous.sortField) {
        return {
          ...previous,
          page: 0,
          sortDirection: previous.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }
      return { ...previous, page: 0, sortField: field, sortDirection: defaultDirectionFor(field) };
    });
  }, []);

  const handlePageChange = useCallback((nextPage: number): void => {
    setQuery((previous) => ({ ...previous, page: Math.max(0, nextPage) }));
  }, []);

  const handlePageSizeChange = useCallback((nextSize: number): void => {
    if (!Number.isFinite(nextSize) || nextSize <= 0) {
      return;
    }
    setQuery((previous) => ({ ...previous, page: 0, size: nextSize }));
  }, []);

  /**
   * Benutzerauswahl: ID in den URL-Query schreiben (uebrige Parameter
   * bleiben erhalten) und eine offene PDF-Ansicht schliessen — sie gehoert
   * zum vorherigen Benutzer.
   */
  const handleSelectUser = useCallback(
    (userId: string): void => {
      setPdfContract(null);
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set(USER_QUERY_PARAM, userId);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleShowPdf = useCallback((contract: ContractSummary): void => {
    setPdfContract(contract);
  }, []);

  const handleClosePdf = useCallback((): void => {
    setPdfContract(null);
  }, []);

  return (
    <div id="users-page">
      <h1 id="users-heading" className="page-heading h3 mb-3">
        Benutzer
      </h1>

      <Row id="users-row" className="g-3">
        <Col id="users-list-col" xs={12} lg={7}>
          <UserList
            query={query}
            searchInput={searchInput}
            data={users.data}
            loading={users.loading}
            error={users.error}
            selectedUserId={selectedUserId}
            onSearchChange={handleSearchChange}
            onSearchClear={handleSearchClear}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSelectUser={handleSelectUser}
          />
        </Col>

        <Col id="users-detail-col" xs={12} lg={5}>
          <div id="user-detail-stack" className="d-flex flex-column gap-3">
            <UserDetailPanel
              user={detail.data}
              loading={detail.loading}
              error={detail.error}
              officeExportSaving={officeExport.saving}
              officeExportError={officeExport.error}
              onOfficeExportChange={officeExport.toggle}
            />
            {/*
              Bei unbekannter Benutzer-ID (Deep-Link auf eine geloeschte
              ID, HTTP 404) bleibt die Vertragsliste aus: Neben "Der
              Benutzer wurde nicht gefunden." waere "Keine Mietverträge
              vorhanden." nur irrefuehrend.
            */}
            {selectedUserId !== null && !detail.notFound && (
              <ContractList
                contracts={contracts.data}
                loading={contracts.loading}
                error={contracts.error}
                onShowPdf={handleShowPdf}
              />
            )}
          </div>
        </Col>
      </Row>

      {pdfContract !== null && (
        <ContractPdfViewer contract={pdfContract} onClose={handleClosePdf} />
      )}
    </div>
  );
}

export default UsersPage;
